import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { guardarDestino } from '@/lib/destino';
import { urlFotoFicha } from '@/lib/foto';
import TopoNumeros from './components/TopoNumeros';
import LeitorQR from './components/LeitorQR';
import ResultadoTelaCheia from './components/ResultadoTelaCheia';
import ModalDigitarCodigo from './components/ModalDigitarCodigo';
import ModalBuscarNome from './components/ModalBuscarNome';
import Dica from '@/components/feature/Dica';
import { useModoEquipe } from '@/components/feature/ModoEquipe';
import { iniciarAudio, mensagemBanco, normalizarAcesso, tocarBipes, vibrar } from './utils';
import type {
  ConferenciaIngresso,
  ResumoPortaria,
  ResultadoLeitura,
} from './types';

type EstadoAcesso = 'carregando' | 'permitido' | 'negado' | 'nao-encontrado';

const INTERVALO_RESUMO_MS = 30_000;
const TEMPO_RESULTADO_MS = 2_000;
const REGEX_CODIGO = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function PortariaPage() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const eventoIdNum = Number(eventoId);
  const { ativo: modoEquipe } = useModoEquipe();
  const { user, pronto } = useAuth();

  const [acesso, setAcesso] = useState<EstadoAcesso>('carregando');
  const [eventoNome, setEventoNome] = useState('');
  const [isDono, setIsDono] = useState(false);
  // Quem é da direção vê tudo como sempre; a portaria convidada vê só os números.
  const [direcao, setDirecao] = useState(false);
  const [resumo, setResumo] = useState<ResumoPortaria | null>(null);
  const [codigos, setCodigos] = useState<string[]>([]);

  const [resultado, setResultado] = useState<ResultadoLeitura | null>(null);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [erroCamera, setErroCamera] = useState<string | null>(null);
  const [erroTransitorio, setErroTransitorio] = useState<string | null>(null);
  const [recebendo, setRecebendo] = useState(false);
  const [digitarAberto, setDigitarAberto] = useState(false);
  const [buscarAberto, setBuscarAberto] = useState(false);
  const [iniciada, setIniciada] = useState(false);

  const processandoRef = useRef(false);
  const codigoAtualRef = useRef<string | null>(null);
  const ultimoCodigoRef = useRef<string | null>(null);
  const ultimoTempoRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const erroTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const limparTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Mantém a tela acesa enquanto a portaria estiver aberta.
  useEffect(() => {
    let lock: { release?: () => Promise<void> } | null = null;

    async function adquirir() {
      try {
        const wl = navigator as Navigator & {
          wakeLock?: { request: (t: string) => Promise<{ release?: () => Promise<void> }> };
        };
        lock = await wl.wakeLock?.request('screen');
      } catch {
        // aparelho/navegador sem suporte
      }
    }

    adquirir();
    const aoVisivel = () => {
      if (document.visibilityState === 'visible') adquirir();
    };
    document.addEventListener('visibilitychange', aoVisivel);

    return () => {
      document.removeEventListener('visibilitychange', aoVisivel);
      lock?.release?.();
    };
  }, []);

  const carregarResumo = useCallback(async () => {
    if (Number.isNaN(eventoIdNum)) return;
    try {
      // Direção: números pela view, como sempre.
      if (direcao) {
        const { data, error } = await supabase
          .from('vw_portaria')
          .select('*')
          .eq('evento_id', eventoIdNum)
          .maybeSingle();
        if (!error && data) setResumo(data as ResumoPortaria);
        return;
      }
      // Portaria convidada: números vêm do próprio acesso.
      const { data, error } = await supabase.rpc('portaria_acesso', { p_evento: eventoIdNum });
      if (error) return;
      const a = normalizarAcesso(data);
      if (a?.resumo) setResumo({ evento_id: eventoIdNum, ...a.resumo });
    } catch {
      /* falha silenciosa — o próximo tick tenta de novo */
    }
  }, [eventoIdNum, direcao]);

  const carregarCodigos = useCallback(async () => {
    if (Number.isNaN(eventoIdNum)) return;
    const { data: codigosData } = await supabase
      .from('inscricoes')
      .select('codigo')
      .eq('evento_id', eventoIdNum);
    const listaCodigos = ((codigosData as { codigo: string }[]) || []).map((c) => c.codigo);
    setCodigos(listaCodigos);
  }, [eventoIdNum]);

  // Acesso via `portaria_acesso`: dono, direção ou portaria convidada.
  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino();
      navigate(`/entrar?voltar=${encodeURIComponent(`/portaria/${eventoId}`)}`, {
        replace: true,
      });
      return;
    }
    let ativo = true;

    async function carregar() {
      try {
        if (Number.isNaN(eventoIdNum)) {
          if (ativo) setAcesso('nao-encontrado');
          return;
        }

        const { data, error } = await supabase.rpc('portaria_acesso', { p_evento: eventoIdNum });
        if (error) throw error;
        const a = normalizarAcesso(data);

        // Sem login (mesmo redirecionamento de sempre).
        if (a?.motivo === 'login') {
          guardarDestino();
          navigate(`/entrar?voltar=${encodeURIComponent(`/portaria/${eventoId}`)}`, {
            replace: true,
          });
          return;
        }

        if (!a || a.motivo === 'nao_encontrado') {
          if (ativo) setAcesso('nao-encontrado');
          return;
        }

        if (!a.pode) {
          if (ativo) setAcesso('negado');
          return;
        }

        if (ativo) {
          setEventoNome(a.evento);
          setIsDono(a.dono);
          setDirecao(a.direcao);
          setResumo(a.resumo ? { evento_id: eventoIdNum, ...a.resumo } : null);
          setAcesso('permitido');
        }

        // Só a direção baixa a lista de códigos.
        if (a.direcao) carregarCodigos();
      } catch {
        if (ativo) setAcesso('nao-encontrado');
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, eventoId, eventoIdNum, carregarCodigos, pronto, user]);

  // Atualiza os números a cada 30s.
  useEffect(() => {
    if (acesso !== 'permitido') return;
    carregarResumo();
    const intervalo = setInterval(carregarResumo, INTERVALO_RESUMO_MS);
    return () => clearInterval(intervalo);
  }, [acesso, carregarResumo]);

  // 2) Recarrega a lista de códigos sempre que o modal abrir (só a direção).
  useEffect(() => {
    if (!digitarAberto || !direcao) return;
    carregarCodigos();
  }, [digitarAberto, direcao, carregarCodigos]);

  useEffect(() => {
    return () => {
      limparTimeouts();
      if (erroTimeoutRef.current) clearTimeout(erroTimeoutRef.current);
    };
  }, [limparTimeouts]);

  const exibirResultado = useCallback(
    async (r: ResultadoLeitura) => {
      // som + vibração: liberado = 1 bip agudo; barrado = 2 bips graves.
      if (r.situacao === 'entrou') {
        tocarBipes(1, 880);
        vibrar(80);
      } else {
        tocarBipes(2, 440);
        vibrar([120, 60, 120]);
      }

      const foto = await urlFotoFicha(r.foto_url);
      setFotoUrl(foto);
      setResultado(r);

      if (r.situacao === 'entrou') {
        carregarResumo();
      }

      // Só volta sozinho quando não exige ação ("nao_pago" aguarda o toque).
      if (r.situacao !== 'nao_pago') {
        limparTimeouts();
        timeoutRef.current = setTimeout(() => setResultado(null), TEMPO_RESULTADO_MS);
      }
    },
    [carregarResumo, limparTimeouts],
  );

  const processarCodigo = useCallback(
    async (codigoUuid: string) => {
      const agora = Date.now();
      if (processandoRef.current) return;
      if (codigoUuid === ultimoCodigoRef.current && agora - ultimoTempoRef.current < 4_000) {
        return;
      }
      ultimoCodigoRef.current = codigoUuid;
      ultimoTempoRef.current = agora;
      codigoAtualRef.current = codigoUuid;

      // 1) Valida o formato antes de qualquer chamada — código inválido vira
      //    "INGRESSO NÃO ENCONTRADO" localmente, sem gastar rede.
      if (!REGEX_CODIGO.test(codigoUuid)) {
        await exibirResultado({
          situacao: 'nao_encontrado',
          mensagem: 'INGRESSO NÃO ENCONTRADO',
          participante: null,
          cpf: null,
          foto_url: null,
          participacao: null,
          entrou_em: null,
        });
        return;
      }

      processandoRef.current = true;

      try {
        const { data, error } = await supabase.rpc('registrar_entrada', {
          p_codigo: codigoUuid,
          p_evento: eventoIdNum,
        });
        if (error) throw error;
        const r = (data as ResultadoLeitura[])?.[0];
        if (!r) throw new Error('Resposta inesperada.');
        await exibirResultado(r);
      } catch {
        // Sem sinal: não libera ninguém, só avisa e segue com a câmera.
        setErroTransitorio('Sem sinal — tente de novo');
        if (erroTimeoutRef.current) clearTimeout(erroTimeoutRef.current);
        erroTimeoutRef.current = setTimeout(() => setErroTransitorio(null), 2_500);
      } finally {
        processandoRef.current = false;
      }
    },
    [exibirResultado, eventoIdNum],
  );

  // Portaria convidada: acha o código no banco (não baixa a lista).
  const buscarNoServidor = useCallback(
    async (texto: string) => {
      const { data, error } = await supabase.rpc('portaria_achar_codigo', {
        p_evento: eventoIdNum,
        p_texto: texto,
      });
      if (error) throw error;
      const r = data as { ok?: boolean; codigo?: string; erro?: string } | null;
      return { ok: r?.ok === true, codigo: r?.codigo, erro: r?.erro };
    },
    [eventoIdNum],
  );

  async function receberELiberar() {
    const codigo = codigoAtualRef.current;
    if (!codigo) return;
    setRecebendo(true);
    try {
      const { data: conf, error: errConf } = await supabase.rpc('conferir_ingresso', {
        p_codigo: codigo,
        p_evento: eventoIdNum,
      });
      if (errConf) throw errConf;
      const c = (conf as ConferenciaIngresso[])?.[0];
      const inscricaoId = c?.inscricao_id;
      if (!inscricaoId) throw new Error('Não foi possível identificar a inscrição.');

      const { data: saldoData } = await supabase
        .from('vw_saldo_inscricao')
        .select('saldo_centavos')
        .eq('inscricao_id', inscricaoId)
        .maybeSingle();
      const saldoCentavos =
        (saldoData as { saldo_centavos: number } | null)?.saldo_centavos ?? 0;

      // 4) Se já estiver quitado (saldo <= 0), pula o pagamento e vai direto à
      //    entrada — o segundo toque depois de uma falha de rede não pode travar.
      if (saldoCentavos > 0) {
        const { error: errPag } = await supabase.rpc('registrar_pagamento', {
          p_inscricao: inscricaoId,
          p_valor_reais: saldoCentavos / 100,
          p_metodo: 'dinheiro',
        });
        if (errPag) throw errPag;
      }

      const { data: reg, error: errReg } = await supabase.rpc('registrar_entrada', {
        p_codigo: codigo,
        p_evento: eventoIdNum,
      });
      if (errReg) throw errReg;
      const r = (reg as ResultadoLeitura[])?.[0];
      if (!r) throw new Error('Resposta inesperada.');
      await exibirResultado(r);
    } catch (e) {
      setErroTransitorio(mensagemBanco(e));
    } finally {
      setRecebendo(false);
    }
  }

  function fecharResultado() {
    limparTimeouts();
    setResultado(null);
  }

  // 3) Primeiro toque do usuário: acorda o AudioContext (iOS exige isso).
  function iniciarPortaria() {
    iniciarAudio();
    setIniciada(true);
  }

  if (acesso === 'carregando') {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-black">
        <span className="flex h-14 w-14 items-center justify-center text-white/60">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </span>
      </div>
    );
  }

  if (acesso === 'negado') {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-black px-4">
        <div className="w-full max-w-[420px] rounded-lg border border-white/10 bg-white/5 p-8 text-center">
          <h1 className="text-xl font-medium text-white">Sem acesso</h1>
          <p className="mt-2 text-sm text-white/60">Você não tem acesso à portaria deste evento.</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-white px-6 py-3 text-sm font-medium text-black transition-colors hover:bg-white/90"
          >
            Voltar à vitrine
          </button>
        </div>
      </div>
    );
  }

  if (acesso === 'nao-encontrado') {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-black px-4">
        <div className="w-full max-w-[420px] rounded-lg border border-white/10 bg-white/5 p-8 text-center">
          <h1 className="text-xl font-medium text-white">Evento não encontrado</h1>
          <p className="mt-2 text-sm text-white/60">
            Este evento não existe ou não está mais disponível.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-white px-6 py-3 text-sm font-medium text-black transition-colors hover:bg-white/90"
          >
            Voltar à vitrine
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-[100dvh] flex-col bg-black">
      {!modoEquipe && (
        <button
          type="button"
          onClick={() => (direcao ? navigate(`/gerenciar/${eventoId}`) : navigate('/'))}
          className="absolute left-3 top-3 z-30 flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/20"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-left-line" />
          </span>
          {direcao ? 'Painel' : 'Início'}
        </button>
      )}

      <div className="absolute right-3 top-3 z-30">
        <Dica id="portaria" claro />
      </div>

      <TopoNumeros resumo={resumo} eventoNome={eventoNome} />

      {/* Câmera ocupa o resto */}
      <div className="relative flex-1">
        {!iniciada ? (
          <div className="flex h-full flex-col items-center justify-center gap-6 px-6 text-center">
            <p className="text-sm text-white/60">
              Toque para ligar a câmera e o som antes de começar a fila.
            </p>
            <button
              type="button"
              onClick={iniciarPortaria}
              className="cursor-pointer whitespace-nowrap rounded-lg bg-green-600 px-10 py-6 text-2xl font-bold text-white transition-transform active:scale-[0.98]"
            >
              Iniciar portaria
            </button>
          </div>
        ) : erroCamera ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 text-white/70">
              <i className="ri-camera-off-line text-3xl" />
            </span>
            <p className="text-sm text-white/70">
              Não foi possível abrir a câmera. Verifique a permissão ou use o código digitado.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setDigitarAberto(true)}
                className="cursor-pointer whitespace-nowrap rounded-md bg-white px-6 py-3 text-sm font-medium text-black transition-colors hover:bg-white/90"
              >
                Digitar código
              </button>
              <button
                type="button"
                onClick={() => setBuscarAberto(true)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-white/40 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
              >
                <span className="mr-2 inline-flex h-4 w-4 items-center justify-center align-middle">
                  <i className="ri-user-search-line" />
                </span>
                Buscar por nome
              </button>
            </div>
          </div>
        ) : (
          <LeitorQR
            ativo={!resultado}
            aoLer={processarCodigo}
            aoErroCamera={(msg) => setErroCamera(msg)}
          />
        )}

        {/* Aviso de sem sinal */}
        {erroTransitorio && (
          <div className="absolute left-0 right-0 top-0 z-20 bg-red-600 px-4 py-3 text-center text-sm font-semibold text-white">
            {erroTransitorio}
          </div>
        )}
      </div>

      {/* Rodapé */}
      <div className="shrink-0 border-t border-white/10 bg-black/40 px-4 py-3">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setDigitarAberto(true)}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-white/25 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
          >
            <span className="mr-2 inline-flex h-4 w-4 items-center justify-center align-middle">
              <i className="ri-keyboard-line" />
            </span>
            Digitar código
          </button>
          <button
            type="button"
            onClick={() => setBuscarAberto(true)}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-white/25 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
          >
            <span className="mr-2 inline-flex h-4 w-4 items-center justify-center align-middle">
              <i className="ri-user-search-line" />
            </span>
            Buscar por nome
          </button>
        </div>
      </div>

      {resultado && (
        <ResultadoTelaCheia
          resultado={resultado}
          fotoUrl={fotoUrl}
          isDono={isDono}
          recebendo={recebendo}
          aoReceberLiberar={receberELiberar}
          aoVoltar={fecharResultado}
        />
      )}

      {digitarAberto && (
        <ModalDigitarCodigo
          codigos={codigos}
          aoBuscar={direcao ? undefined : buscarNoServidor}
          aoLiberar={(uuid) => {
            setDigitarAberto(false);
            processarCodigo(uuid);
          }}
          aoFechar={() => setDigitarAberto(false)}
        />
      )}

      {buscarAberto && (
        <ModalBuscarNome
          eventoId={eventoIdNum}
          aoDarEntrada={(codigo) => {
            setBuscarAberto(false);
            processarCodigo(codigo);
          }}
          aoFechar={() => setBuscarAberto(false)}
        />
      )}
    </div>
  );
}